import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { chartColors } from '@src/constants/chartColors';

type InUseSim = {
    date: string;
    accountName: string;
    totalInUse: number;
};

type InUseStackedBarChartProps = {
    data: InUseSim[];
    height?: number;
    colors?: string[];
};

export function InUseStackedBarChart({
    data,
    height = 230,
    colors = chartColors,
}: InUseStackedBarChartProps) {
    const chartRef = useRef<HTMLDivElement>(null);

    function stringToColor(str: string) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }

    useEffect(() => {
        if (!chartRef.current) return;

        const categories = Array.from(
            new Set(data.map((item) => dayjs(item.date).format('MMM')))
        ).sort();

        const accounts = Array.from(
            new Set(data.map((item) => item.accountName))
        );

        const series = accounts.map((accountName, seriesIndex) => ({
            name: accountName,
            type: 'bar',
            stack: 'total',
            barWidth: 6,
            data: categories.map((month) => {
                const record = data.find(
                    (item) =>
                        item.accountName === accountName &&
                        dayjs(item.date).format('MMM') === month
                );
                const value = record ? record.totalInUse : 0;

                const topmostIndexForMonth = (() => {
                    for (let i = accounts.length - 1; i >= 0; i--) {
                        const otherAccount = accounts[i];
                        const otherRecord = data.find(
                            (item) =>
                                item.accountName === otherAccount &&
                                dayjs(item.date).format('MMM') === month
                        );
                        if (otherRecord && otherRecord.totalInUse > 0) return i;
                    }
                    return -1;
                })();

                return {
                    value,
                    itemStyle: {
                        color: stringToColor(accountName),
                        borderRadius:
                            seriesIndex === topmostIndexForMonth && value > 0
                                ? [16, 16, 0, 0]
                                : 0,
                    },
                };
            }),
            itemStyle: {
                color: stringToColor(accountName),
                borderRadius: [16, 16, 0, 0],
            },
        }));

        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: function (params) {
                    const filtered = params.filter(
                        (item) => item.value && item.value !== 0
                    );

                    if (filtered.length === 0) return '';

                    return filtered
                        .map(
                            (item) =>
                                `${item.marker} <span style="font-weight: normal;">${item.seriesName}</span> <span style="font-weight: bold;">${item.value} SIMs</span>`
                        )
                        .join('<br/>');
                },
            },
            legend: {
                data: accounts,
                icon: 'circle',
            },
            xAxis: {
                type: 'category',
                data: categories,
            },
            yAxis: {
                type: 'value',
                name: 'SIMs',
            },
            series,
        };

        const chartInstance = echarts.init(chartRef.current);
        chartInstance.setOption(option);

        return () => {
            chartInstance.dispose();
        };
    }, [data]);

    return <div ref={chartRef} style={{ height, width: '100%' }} />;
}
